import { AfterViewInit, Component, ElementRef, NgZone, OnInit, ViewChild } from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {FormControl} from "@angular/forms";
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDatepicker } from "@angular/material";
import {MomentDateAdapter} from "@angular/material-moment-adapter";
import {MonthlySpending} from "src/app/api";
import * as moment from "moment";
import { distinctUntilChanged, startWith, switchMap, tap } from "rxjs/operators";
import {UiRouterState} from "../../services/ajs-router-state.service";
import {StateParamsService} from "../../services/ajs-state-params.service";
import {TourService} from "../../tour/tour.service";
import {environment} from "../../../environments/environment";
import {LocalStorageService} from "../../services/localstorage.service";

export const MY_FORMATS = {
  parse: {
    dateInput: "MMMM/YYYY",
  },
  display: {
    dateInput: "MMMM/YYYY",
    monthYearLabel: "MMM YYYY",
    dateA11yLabel: "LL",
    monthYearA11yLabel: "MMMM YYYY",
  },
};

declare var google;

@Component({
  selector: "mandae-financial-dashboard",
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.scss"],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    {provide: MAT_DATE_FORMATS, useValue: MY_FORMATS},
  ],
})
export class DashboardComponent implements OnInit, AfterViewInit {
  public date = new FormControl(moment());

  @ViewChild("chartContainer")
  public chartContainer: ElementRef<HTMLDivElement>;

  public gastoMensal = 0;

  public get intCost() {
    return Math.floor(this.gastoMensal);
  }

  public get cents() {
    return Math.floor((this.gastoMensal - Math.floor(this.gastoMensal)) * 100);
  }

  private dataset: MonthlySpending = {
    complementoMinimo: 0,
    gastoDiario: [],
  };

  private URL_GASTO_MENSAL_DASHBOARD: string =
    environment.angularJSconstants.URL_SERVICO_FISCAL;

  constructor(
    private ngZone: NgZone,
    private tour: TourService,
    private router: UiRouterState,
    private stateParams: StateParamsService,
    private http: HttpClient,
    private localStorageService: LocalStorageService
  ) {
  }

  ngOnInit() {
    this.initializeCharts();

    this.date.valueChanges
      .pipe(
        startWith(moment()),
        distinctUntilChanged(
          (x: moment.Moment, y: moment.Moment) =>
            !x || !y || x.format("YYYY-MM") === y.format("YYYY-MM")
        ),
        switchMap((date: moment.Moment) =>
          this.getGastoMensal(date.format("YYYY-MM"))
        ),
        tap((data) => {
          const currentDate = <moment.Moment>this.date.value;
          const lastDay = currentDate.endOf("month").date();
          this.dataset = {
            // Preenche array com todos os dias para o mês
            gastoDiario: Array.from(Array(lastDay).keys())
              .map((dia) => ({data: moment(currentDate).date(dia + 1)}))
              // Atribui o valor do dia
              .map((dia) => {
                const diaFormatado = dia.data.format("YYYY-MM-DD");
                const dataDia = data.gastoDiario.find(
                  (x) => x.data === diaFormatado
                );
                const valor = dataDia
                  ? dataDia.valor
                  : dia.data.isAfter(moment(), "day")
                    ? null
                    : 0;
                return {data: dia.data, valor};
              })
              // Agrega o valor (valor D+1 = valor D+1 + valor D)
              .reduce((agg, cur, i) => {
                agg.push({
                  data: cur.data,
                  valor:
                    cur.valor !== null
                      ? cur.valor + (i > 0 ? agg[i - 1].valor : 0)
                      : null,
                });

                return agg;
              }, []),
            complementoMinimo: data.complementoMinimo,
          };

          this.gastoMensal = data.gastoDiario.reduce(
            (total, dia) => total + dia.valor,
            0
          );
          setTimeout(() => {
            this.initializeCharts();
          }, 0);
        })
      )
      .subscribe();
  }

  public async getGastoMensal(mesReferencia): Promise<any> {
    const user = this.localStorageService.get("user");
    return this.http
      .get(
        `${this.URL_GASTO_MENSAL_DASHBOARD}/v2/extratos/mensal/detalhado?referenceMonth=${mesReferencia}`,
        {responseType: 'json'}
      )
      .toPromise();
  }

  ngAfterViewInit() {
    if (this.stateParams.tour) {
      this.startTour();
    }
  }

  chosenMonthHandler(
    normalizedMonth: moment.Moment,
    datepicker: MatDatepicker<moment.Moment>
  ) {
    this.date.setValue(moment(normalizedMonth));
    datepicker.close();
  }

  private initializeCharts() {
    if (typeof google === "undefined") {
      return;
    }

    google.charts.load("current", {
      packages: ["corechart"],
      language: "pt",
    });
    google.charts.setOnLoadCallback(() => this.createChart());
  }

  private createChart() {
    const data = new google.visualization.DataTable();
    data.addColumn("date", "Data");
    data.addColumn("number", "Gastos com envios");

    const temFaturamentoMinimo = this.dataset.complementoMinimo > 0;

    let vaxisMaxTick;
    let vaxisTicks;

    if (temFaturamentoMinimo) {
      vaxisMaxTick =
        Math.max(
          2,
          Math.ceil(this.gastoMensal / this.dataset.complementoMinimo)
        ) * this.dataset.complementoMinimo;
      vaxisTicks = Array.from(
        Array(vaxisMaxTick / this.dataset.complementoMinimo + 1).keys()
      ).map((v) => v * this.dataset.complementoMinimo);

      data.addColumn("number", "Faturamento mínimo");
      data.addRows(
        this.dataset.gastoDiario.map((x) => [
          moment(x.data).toDate(),
          x.valor,
          this.dataset.complementoMinimo,
        ])
      );
    } else {
      vaxisMaxTick = this.gastoMensal;
      vaxisTicks = [0, 0.5, 1].map((x) => x * this.gastoMensal);

      data.addRows(
        this.dataset.gastoDiario.map((x) => [moment(x.data).toDate(), x.valor])
      );
    }

    const haxisTicks = Array.from(Array(this.dataset.gastoDiario.length).keys())
      .filter((x) => x % 2 === 0)
      .map((x) =>
        moment(this.dataset.gastoDiario[0].data).add(x, "day").toDate()
      );

    const options = {
      height: 400,
      hAxis: {
        title: "Data",
        format: "dd",
        gridlines: {
          count: Math.ceil(this.dataset.gastoDiario.length / 2),
          color: "none",
        },
        minValue: this.dataset.gastoDiario
          ? moment(this.dataset.gastoDiario[0].data).toDate()
          : null,
        maxValue: moment(
          this.dataset.gastoDiario[this.dataset.gastoDiario.length - 1].data
        ).toDate(),
        ticks: haxisTicks,
      },
      vAxis: {
        title: "Valor (R$)",
        ticks: vaxisTicks,
        minValue: 0,
      },
      seriesType: "area",
      series: {
        0: {color: "#00A9CE"},
        1: {type: "line"},
      },
      allowHtml: true,
    };

    this.ngZone.runOutsideAngular(() => {
      const chart = new google.visualization.ComboChart(
        this.chartContainer.nativeElement
      );

      const dateFormatter = new google.visualization.DateFormat({
        pattern: "dd/MM/yyyy",
      });
      const currencyFormatter = new google.visualization.NumberFormat({
        prefix: "R$ ",
        negativeParens: false,
        groupingSymbol: ".",
        decimalSymbol: ",",
      });

      dateFormatter.format(data, 0);
      currencyFormatter.format(data, 1);
      if (temFaturamentoMinimo) {
        currencyFormatter.format(data, 2);
      }

      chart.draw(data, options);
    });
  }

  private startTour() {
    const steps = this.tour.makeTour([
      {
        title: "Controle financeiro",
        text: "Acompanhe a evolução dos seus gastos, baixe suas faturas e controle seus reembolsos.",
        attachTarget: () =>
          document.querySelector<HTMLElement>(
            '.menu-container a[href*="/financeiro"]'
          ),
      },
      {
        title: "Resumo da fatura",
        text: "Tenha uma visão resumida dos gastos até a data atual.",
        attachTarget: () =>
          document.querySelector<HTMLElement>(
            "app-toolbar .mat-tab-label-active"
          ),
        next: {
          text: "Ir para Fatura",
          action: (ev, ref) => {
            ref.close("to_next_section");
            this.router.go("faturas", {tour: true});
          },
        },
      },
    ]);

    this.tour.start(steps).subscribe();
  }
}
